import {
  getStoreKeyName,
  storeKeyNameFromField
} from '@apollo/client/utilities/internal'
import type {
  FieldFunctionOptions,
  FieldPolicy,
  Reference
} from '@apollo/client/cache'
import { uniqBy } from 'lodash'

type TRelayPageInfo = {
  hasPreviousPage: boolean
  hasNextPage: boolean
  startCursor: string | null
  endCursor: string | null
}

type TRelayEdge<TNode> = {
  cursor?: string
  node: TNode
}

type RelayConnection<TNode> = Readonly<{
  edges: TRelayEdge<TNode>[]
  pageInfo: TRelayPageInfo
}>

interface RelayStylePaginationConfigurationOptions {
  excludeKeyArgs?: string[]
  uniqByValueIteratee?: (
    edge: TRelayEdge<any>,
    readField: FieldFunctionOptions['readField']
  ) => unknown
}

export const relayStylePagination = <TNode = Reference>({
  excludeKeyArgs = ['after', 'before', 'first', 'last'], // exclude "paginated" variables
  uniqByValueIteratee = (edge, readField) => {
    // 'node.id'
    return readField('id', readField('node', edge))
  }
}: RelayStylePaginationConfigurationOptions): FieldPolicy<
  RelayConnection<TNode>
> => {
  return {
    keyArgs: (args, context) => {
      // console.log('keyArgs', { args, context })
      if (context.field) {
        const hasDirectives = context.field.directives?.length
        const hasExcludedVariables = excludeKeyArgs.some(
          excludeKeyArg => args && Object.hasOwn(args, excludeKeyArg)
        )
        //  - when query use directive (like @connection) we can use default apollo cache key
        //  - when query doesn't have "excluded" variables we can use default apollo cache key
        if (hasDirectives || !hasExcludedVariables) {
          return storeKeyNameFromField(context.field, context.variables)
        }

        const variablesWithoutExcludeKeyArgs = Object.fromEntries(
          Object.entries(args || {}).filter(
            ([key]) => !excludeKeyArgs.includes(key)
          )
        )
        return storeKeyNameFromField(
          context.field,
          variablesWithoutExcludeKeyArgs
        )
      }

      return getStoreKeyName(context.fieldName, args || context.variables)
    },

    merge(existing, incoming, { args, readField }) {
      // when no previous cashed data exist
      if (!existing) {
        return incoming
      }

      // when we fetch first page we only need new data
      if (!args?.after) {
        return incoming
      }

      const existingEdges = existing.edges || []
      const incomingEdges = incoming.edges || []

      const uniqEdges = uniqBy([...existingEdges, ...incomingEdges], edge =>
        uniqByValueIteratee(edge, readField)
      )

      return {
        ...existing,
        ...incoming,
        // in some cases server return the same items (with the same id) on different pages, so we need filter-out duplicates
        edges: uniqEdges
      }
    }
  }
}
